import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { format, subDays, isAfter, isBefore, startOfDay } from "date-fns";
import {
  Activity,
  TrendingDown,
  TrendingUp,
  Trash2,
  ShoppingCart,
  ArrowRightLeft,
  Clock,
  User,
  Package,
  RefreshCw,
  CalendarIcon,
  PackagePlus,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * StockDiagnosticDialog - Shows stock changes for a shop over a time period
 * Helps investigate unexpected stock discrepancies
 */
export default function StockDiagnosticDialog({ open, onOpenChange, shops, selectedShopId }) {
  const [shopId, setShopId] = useState(selectedShopId || "");
  const [hours, setHours] = useState("1");
  const [endDate, setEndDate] = useState(new Date());
  const [endHour, setEndHour] = useState(new Date().getHours().toString().padStart(2, '0'));
  const [endMinute, setEndMinute] = useState(new Date().getMinutes().toString().padStart(2, '0'));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState("deleted");
  const [expandedSales, setExpandedSales] = useState({});

  // Toggle sale expansion
  const toggleSaleExpanded = (saleIdx) => {
    setExpandedSales(prev => ({
      ...prev,
      [saleIdx]: !prev[saleIdx]
    }));
  };

  // Calculate min date (30 days ago)
  const minDate = subDays(new Date(), 30);

  // Update shopId when selectedShopId changes
  useEffect(() => {
    if (selectedShopId) {
      setShopId(selectedShopId);
    }
  }, [selectedShopId]);

  // Reset data and set current time when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setData(null);
    } else {
      // Set current time when dialog opens
      const now = new Date();
      setEndDate(now);
      setEndHour(now.getHours().toString().padStart(2, '0'));
      setEndMinute(now.getMinutes().toString().padStart(2, '0'));
    }
  }, [open]);

  // Generate hour options (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  
  // Generate minute options (00-59)
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
  
  // Generate hours back options (1-24)
  const hoursBackOptions = Array.from({ length: 24 }, (_, i) => (i + 1).toString());

  // Check if a given hour/minute is in the future for today
  const isTimeFuture = (hour, minute) => {
    const now = new Date();
    const selectedDate = new Date(endDate);
    
    // If not today, no time restriction
    if (format(selectedDate, 'yyyy-MM-dd') !== format(now, 'yyyy-MM-dd')) {
      return false;
    }
    
    const nowHour = now.getHours();
    const nowMinute = now.getMinutes();
    
    if (parseInt(hour) > nowHour) return true;
    if (parseInt(hour) === nowHour && parseInt(minute) > nowMinute) return true;
    return false;
  };

  const fetchDiagnostic = async () => {
    if (!shopId) {
      toast.error("Please select a shop");
      return;
    }
    
    // Construct end datetime
    const endDateTime = new Date(endDate);
    endDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
    
    // Validate: cannot be in the future
    if (isAfter(endDateTime, new Date())) {
      toast.error("End date/time cannot be in the future");
      return;
    }
    
    // Validate: cannot be more than 30 days ago
    if (isBefore(endDateTime, minDate)) {
      toast.error("End date/time cannot be more than 30 days in the past");
      return;
    }
    
    setLoading(true);
    try {
      const { data: result } = await api.get(`/stock/diagnostic/changes/${shopId}`, {
        params: { 
          hours: parseInt(hours),
          end_datetime: endDateTime.toISOString()
        }
      });
      setData(result);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "—";
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="stock-diagnostic-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#9E6D5B]" />
            Stock Change Diagnostic
          </DialogTitle>
          <DialogDescription>
            Investigate stock changes for a shop over a specific time period
          </DialogDescription>
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-end py-2 border-b">
          <div className="min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Shop</Label>
            <Select value={shopId} onValueChange={setShopId}>
              <SelectTrigger data-testid="diagnostic-shop-select">
                <SelectValue placeholder="Select Shop" />
              </SelectTrigger>
              <SelectContent>
                {shops.map((shop) => (
                  <SelectItem key={shop.id} value={shop.id}>{shop.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* End Date Picker */}
          <div className="min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !endDate && "text-muted-foreground"
                  )}
                  data-testid="diagnostic-end-date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "MMM dd, yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => date && setEndDate(date)}
                  disabled={(date) => 
                    isAfter(startOfDay(date), startOfDay(new Date())) || 
                    isBefore(date, minDate)
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Time Picker */}
          <div className="flex gap-1">
            <div className="w-16">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Hour</Label>
              <Select value={endHour} onValueChange={setEndHour}>
                <SelectTrigger data-testid="diagnostic-end-hour">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {hourOptions.map((h) => (
                    <SelectItem 
                      key={h} 
                      value={h}
                      disabled={isTimeFuture(h, endMinute)}
                    >
                      {h}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-16">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Min</Label>
              <Select value={endMinute} onValueChange={setEndMinute}>
                <SelectTrigger data-testid="diagnostic-end-minute">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {minuteOptions.map((m) => (
                    <SelectItem 
                      key={m} 
                      value={m}
                      disabled={isTimeFuture(endHour, m)}
                    >
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Hours Lookback Dropdown */}
          <div className="w-24">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Hours Back</Label>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger data-testid="diagnostic-hours-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-48">
                {hoursBackOptions.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={fetchDiagnostic} 
            disabled={loading || !shopId}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="diagnostic-run-btn"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
            ) : (
              <><Activity className="w-4 h-4 mr-2" /> Run Diagnostic</>
            )}
          </Button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {!data && !loading && (
            <div className="text-center text-muted-foreground py-12">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Select a shop and time range, then click "Run Diagnostic"</p>
            </div>
          )}

          {data && (
            <>
              {/* Summary Cards - 5 cards in a row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {/* Net Change */}
                <Card className={`p-3 ${data.summary.net_quantity_change < 0 ? "border-red-200 bg-red-50/50" : data.summary.net_quantity_change > 0 ? "border-green-200 bg-green-50/50" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {data.summary.net_quantity_change < 0 ? (
                      <TrendingDown className="w-4 h-4 text-red-600" />
                    ) : (
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Change</span>
                  </div>
                  <div className={`text-xl font-bold ${data.summary.net_quantity_change < 0 ? "text-red-600" : data.summary.net_quantity_change > 0 ? "text-green-600" : ""}`}>
                    {data.summary.net_quantity_change > 0 ? "+" : ""}{data.summary.net_quantity_change}
                  </div>
                </Card>
                
                {/* Deleted */}
                <Card 
                  className={`p-3 border-red-200 bg-red-50/30 cursor-pointer transition-all ${activeDetailTab === 'deleted' ? 'ring-2 ring-red-400' : 'hover:bg-red-50/50'}`}
                  onClick={() => setActiveDetailTab('deleted')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Trash2 className="w-4 h-4 text-red-500" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Deleted</span>
                  </div>
                  <div className="text-xl font-bold text-red-600">-{data.summary.deleted_quantity || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{data.summary.deleted_batches} batches</div>
                </Card>
                
                {/* Sales */}
                <Card 
                  className={`p-3 border-blue-200 bg-blue-50/30 cursor-pointer transition-all ${activeDetailTab === 'sales' ? 'ring-2 ring-blue-400' : 'hover:bg-blue-50/50'}`}
                  onClick={() => setActiveDetailTab('sales')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ShoppingCart className="w-4 h-4 text-blue-500" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sales</span>
                  </div>
                  <div className="text-xl font-bold text-blue-600">-{data.summary.sales_quantity || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{data.summary.sales_count} transactions</div>
                </Card>
                
                {/* Transfers */}
                <Card 
                  className={`p-3 border-purple-200 bg-purple-50/30 cursor-pointer transition-all ${activeDetailTab === 'transfers' ? 'ring-2 ring-purple-400' : 'hover:bg-purple-50/50'}`}
                  onClick={() => setActiveDetailTab('transfers')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowRightLeft className="w-4 h-4 text-purple-500" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Transfers</span>
                  </div>
                  <div className="text-xl font-bold text-purple-600">
                    {data.summary.transfers_in > 0 ? `+${data.summary.transfers_in}` : "0"} / {data.summary.transfers_out > 0 ? `-${data.summary.transfers_out}` : "0"}
                  </div>
                  <div className="text-[10px] text-muted-foreground">in / out</div>
                </Card>

                {/* Stock Update */}
                <Card 
                  className={`p-3 border-amber-200 bg-amber-50/30 cursor-pointer transition-all ${activeDetailTab === 'updates' ? 'ring-2 ring-amber-400' : 'hover:bg-amber-50/50'}`}
                  onClick={() => setActiveDetailTab('updates')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <PackagePlus className="w-4 h-4 text-amber-600" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock Update</span>
                  </div>
                  <div className="text-xl font-bold text-amber-600">
                    {data.summary.stock_updates_added > 0 ? `+${data.summary.stock_updates_added}` : "0"} / {data.summary.stock_updates_reduced > 0 ? `-${data.summary.stock_updates_reduced}` : "0"}
                  </div>
                  <div className="text-[10px] text-muted-foreground">added / reduced</div>
                </Card>
              </div>

              {/* Tabbed Details Section */}
              <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-3">
                  <TabsTrigger value="deleted" className="text-xs gap-1">
                    <Trash2 className="w-3 h-3" /> Deleted
                  </TabsTrigger>
                  <TabsTrigger value="sales" className="text-xs gap-1">
                    <ShoppingCart className="w-3 h-3" /> Sales
                  </TabsTrigger>
                  <TabsTrigger value="transfers" className="text-xs gap-1">
                    <ArrowRightLeft className="w-3 h-3" /> Transfers
                  </TabsTrigger>
                  <TabsTrigger value="updates" className="text-xs gap-1">
                    <PackagePlus className="w-3 h-3" /> Stock Update
                  </TabsTrigger>
                </TabsList>

                {/* Deleted Tab */}
                <TabsContent value="deleted" className="mt-0">
                  {(() => {
                    const deletedEntries = data.stock_log_entries.filter(e => e.change_type?.toLowerCase().includes("delete"));
                    if (deletedEntries.length === 0) {
                      return (
                        <Card className="p-6 text-center border-red-100">
                          <Trash2 className="w-8 h-8 mx-auto mb-2 text-red-200" />
                          <p className="text-sm text-muted-foreground">No stock deletions in this period</p>
                        </Card>
                      );
                    }
                    return (
                      <Card className="p-3 border-red-200">
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {deletedEntries.map((entry, idx) => (
                            <div key={entry.batch_id || `del-${idx}`} className="flex items-start gap-3 p-2 rounded bg-red-50/50 border border-red-100">
                              <Badge variant="outline" className="text-[10px] border-red-300 text-red-700 shrink-0">
                                −{Math.abs(entry.old_quantity)}
                              </Badge>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{entry.product_name || "Unknown Product"}</div>
                                {entry.auto_barcode && (
                                  <div className="text-[10px] text-muted-foreground font-mono">{entry.auto_barcode}</div>
                                )}
                                <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(entry.time)}</span>
                                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{entry.user || "Unknown"}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })()}
                </TabsContent>

                {/* Sales Tab */}
                <TabsContent value="sales" className="mt-0">
                  {data.sales_in_period.length === 0 ? (
                    <Card className="p-6 text-center border-blue-100">
                      <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-blue-200" />
                      <p className="text-sm text-muted-foreground">No sales in this period</p>
                    </Card>
                  ) : (
                    <Card className="p-3 border-blue-200">
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {data.sales_in_period.map((sale, idx) => (
                          <Collapsible 
                            key={sale.invoice_number || `sale-${idx}`}
                            open={expandedSales[idx]}
                            onOpenChange={() => toggleSaleExpanded(idx)}
                          >
                            <CollapsibleTrigger asChild>
                              <div className="flex items-start gap-3 p-2 rounded bg-blue-50/50 border border-blue-100 cursor-pointer hover:bg-blue-100/50 transition-colors">
                                <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 shrink-0">
                                  −{sale.quantity_sold} qty
                                </Badge>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium flex items-center gap-2">
                                    {sale.invoice_number || "No Invoice #"} · ₹{Number(sale.total || 0).toFixed(2)}
                                    {expandedSales[idx] ? (
                                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                    )}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(sale.time)}</span>
                                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{sale.user || "Unknown"}</span>
                                    <span className="flex items-center gap-1"><Package className="w-3 h-3" />{sale.items_count} items</span>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              {/* Line Items Sub-tree */}
                              <div className="ml-6 mt-1 pl-3 border-l-2 border-blue-200 space-y-1">
                                {(sale.line_items || []).map((item, itemIdx) => (
                                  <div 
                                    key={`${idx}-item-${itemIdx}`} 
                                    className="flex items-center gap-2 p-1.5 rounded bg-white/60 text-xs"
                                  >
                                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0 shrink-0">
                                      ×{item.quantity}
                                    </Badge>
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium truncate block">{item.product_name}</span>
                                      {item.auto_barcode && (
                                        <span className="text-[10px] text-muted-foreground font-mono">{item.auto_barcode}</span>
                                      )}
                                    </div>
                                    <span className="text-muted-foreground shrink-0">
                                      ₹{Number(item.price || 0).toFixed(2)} each
                                    </span>
                                    <span className="font-medium text-blue-700 shrink-0">
                                      = ₹{Number(item.total || item.quantity * item.price || 0).toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        ))}
                      </div>
                    </Card>
                  )}
                </TabsContent>

                {/* Transfers Tab */}
                <TabsContent value="transfers" className="mt-0">
                  {data.stock_transfers_in.length === 0 && data.stock_transfers_out.length === 0 ? (
                    <Card className="p-6 text-center border-purple-100">
                      <ArrowRightLeft className="w-8 h-8 mx-auto mb-2 text-purple-200" />
                      <p className="text-sm text-muted-foreground">No transfers in this period</p>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {/* Truncation Warning Banner */}
                      {(data.transfers_in_truncated || data.transfers_out_truncated) && (
                        <Card className="p-3 border-amber-300 bg-amber-50" data-testid="transfer-truncation-warning">
                          <div className="flex items-start gap-2">
                            <Activity className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-amber-800">Display limit reached</p>
                              <p className="text-amber-700 text-xs mt-1">
                                Only the first 500 transfer records are shown below for performance.
                                <strong className="block mt-1">Summary totals above include ALL {data.summary.transfers_in_count + data.summary.transfers_out_count} matching transfer records.</strong>
                              </p>
                              <div className="flex gap-4 mt-2 text-xs">
                                {data.transfers_in_truncated && (
                                  <span className="text-amber-700">
                                    Transfers IN: showing {data.stock_transfers_in.length} of {data.summary.transfers_in_count} records
                                  </span>
                                )}
                                {data.transfers_out_truncated && (
                                  <span className="text-amber-700">
                                    Transfers OUT: showing {data.stock_transfers_out.length} of {data.summary.transfers_out_count} records
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}
                      <div className="grid md:grid-cols-2 gap-3">
                      {/* Transfers IN */}
                      <Card className="p-3 border-green-200">
                        <h4 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Transfers IN ({data.stock_transfers_in.length} records · {data.summary.transfers_in} units)
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {data.stock_transfers_in.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-2">None</p>
                          ) : (
                            data.stock_transfers_in.map((t, idx) => (
                              <div key={`in-${idx}`} className="flex items-start gap-2 p-2 rounded bg-green-50/50 border border-green-100">
                                <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 shrink-0">+{t.quantity}</Badge>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium truncate">{t.product_name}</div>
                                  {t.auto_barcode && (
                                    <div className="text-[10px] text-muted-foreground font-mono">{t.auto_barcode}</div>
                                  )}
                                  <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                    <span>From: {t.from_shop}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(t.time)}</span>
                                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{t.user || "Unknown"}</span>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </Card>
                      {/* Transfers OUT */}
                      <Card className="p-3 border-orange-200">
                        <h4 className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" /> Transfers OUT ({data.stock_transfers_out.length} records · {data.summary.transfers_out} units)
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {data.stock_transfers_out.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-2">None</p>
                          ) : (
                            data.stock_transfers_out.map((t, idx) => (
                              <div key={`out-${idx}`} className="flex items-start gap-2 p-2 rounded bg-orange-50/50 border border-orange-100">
                                <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 shrink-0">-{t.quantity}</Badge>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium truncate">{t.product_name}</div>
                                  {t.auto_barcode && (
                                    <div className="text-[10px] text-muted-foreground font-mono">{t.auto_barcode}</div>
                                  )}
                                  <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                    <span>To: {t.to_shop}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(t.time)}</span>
                                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{t.user || "Unknown"}</span>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </Card>
                    </div>
                    </div>
                  )}
                </TabsContent>

                {/* Stock Updates Tab */}
                <TabsContent value="updates" className="mt-0">
                  {(() => {
                    // Exclude delete, transfer, AND sale entries (sales are shown in Sales tab)
                    const updateEntries = data.stock_log_entries.filter(e => 
                      !e.change_type?.toLowerCase().includes("delete") && 
                      !e.change_type?.toLowerCase().includes("transfer") &&
                      !e.change_type?.toLowerCase().includes("sale")
                    );
                    const additions = updateEntries.filter(e => e.quantity_change > 0);
                    const reductions = updateEntries.filter(e => e.quantity_change < 0);
                    
                    if (additions.length === 0 && reductions.length === 0) {
                      return (
                        <Card className="p-6 text-center border-amber-100">
                          <PackagePlus className="w-8 h-8 mx-auto mb-2 text-amber-200" />
                          <p className="text-sm text-muted-foreground">No stock updates in this period</p>
                        </Card>
                      );
                    }
                    return (
                      <div className="grid md:grid-cols-2 gap-3">
                        {/* Stock Added */}
                        <Card className="p-3 border-green-200">
                          <h4 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> Stock Added ({additions.length})
                          </h4>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {additions.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-2">None</p>
                            ) : (
                              additions.map((entry, idx) => (
                                <div key={`add-${idx}`} className="flex items-start gap-2 p-2 rounded bg-green-50/50 border border-green-100">
                                  <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 shrink-0">+{entry.quantity_change}</Badge>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium truncate">{entry.product_name || "Unknown"}</div>
                                    {entry.auto_barcode && (
                                      <div className="text-[10px] text-muted-foreground font-mono">{entry.auto_barcode}</div>
                                    )}
                                    <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(entry.time)}</span>
                                      <span>{entry.change_type}</span>
                                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{entry.user || "Unknown"}</span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </Card>
                        {/* Stock Reduced */}
                        <Card className="p-3 border-orange-200">
                          <h4 className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                            <TrendingDown className="w-3 h-3" /> Stock Reduced ({reductions.length})
                          </h4>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {reductions.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-2">None</p>
                            ) : (
                              reductions.map((entry, idx) => (
                                <div key={`red-${idx}`} className="flex items-start gap-2 p-2 rounded bg-orange-50/50 border border-orange-100">
                                  <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 shrink-0">{entry.quantity_change}</Badge>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium truncate">{entry.product_name || "Unknown"}</div>
                                    {entry.auto_barcode && (
                                      <div className="text-[10px] text-muted-foreground font-mono">{entry.auto_barcode}</div>
                                    )}
                                    <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(entry.time)}</span>
                                      <span>{entry.change_type}</span>
                                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{entry.user || "Unknown"}</span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </Card>
                      </div>
                    );
                  })()}
                </TabsContent>
              </Tabs>

              {/* No data at all */}
              {data.stock_log_entries.length === 0 && data.sales_in_period.length === 0 && 
               data.stock_transfers_in.length === 0 && data.stock_transfers_out.length === 0 && (
                <Card className="p-8 text-center">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No stock changes found in the selected time period</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Try extending the time range or check a different shop
                  </p>
                </Card>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
